import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faChevronLeft, faChevronRight, faExpand } from '@fortawesome/free-solid-svg-icons';

import eventNetworking from '../asset/events/event-networking.jpg';
import eventLeadership from '../asset/events/event-leadership-summit.jpg';
import eventTableDecor from '../asset/events/event-table-decor.jpg';
import eventBusinessForum from '../asset/events/event-business-forum.jpg';

const galleryItems = [
    {
        id: 1,
        title: 'FM Leadership Dialogue & C-Suite Summit',
        category: 'Corporate MICE & Awards',
        description: 'Keynote leadership conference and executive awards ceremony with industry heads and global delegates.',
        image: eventLeadership,
    },
    {
        id: 2,
        title: 'International Business Forum & Expo',
        category: 'Global Keynote & Forum',
        description: 'Premier international business forum hosting delegations and investors across emerging markets.',
        image: eventBusinessForum,
    },
    {
        id: 3,
        title: 'Curated VIP Dining & Banquet Experience',
        category: 'Bespoke Luxury Hospitality',
        description: 'Intimate, bespoke dinner settings and custom event design for high-net-worth investors and partners.',
        image: eventTableDecor,
    },
    {
        id: 4,
        title: 'Investor Networking & Evening Reception',
        category: 'Private Mixer & Lounge',
        description: 'Curated evening reception fostering strategic discussions between buyers, developers, and advisors.',
        image: eventNetworking,
    },
];

const PastEventsGallery = () => {
    const [selectedIdx, setSelectedIdx] = useState(null);

    // Keyboard navigation (Escape, Left, Right)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedIdx === null) return;
            if (e.key === 'Escape') setSelectedIdx(null);
            if (e.key === 'ArrowRight') setSelectedIdx((prev) => (prev + 1) % galleryItems.length);
            if (e.key === 'ArrowLeft') setSelectedIdx((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedIdx]);

    // Prevent body scroll when lightbox modal is open
    useEffect(() => {
        if (selectedIdx !== null) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedIdx]);

    return (
        <div className="w-full mt-12 sm:mt-16 md:mt-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-12 gap-4">
                <div>
                    <span className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-gray-400">
                        Curated Highlights
                    </span>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-wider text-white mt-1">
                        PAST EVENTS & BESPOKE MICE EXPERIENCES
                    </h2>
                </div>
                <p className="max-w-md text-xs sm:text-sm text-gray-400 leading-relaxed">
                    A glimpse into our curated luxury experiences, international leadership summits, and exclusive investor gatherings hosted worldwide.
                </p>
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                {galleryItems.map((item, idx) => (
                    <div
                        key={item.id}
                        onClick={() => setSelectedIdx(idx)}
                        className="group relative rounded-lg overflow-hidden bg-white/5 border border-white/10 hover:border-white/30 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-2xl flex flex-col"
                    >
                        {/* Image Container */}
                        <div className="relative w-full h-64 sm:h-72 overflow-hidden bg-black/40">
                            <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity duration-300" />
                            
                            {/* Expand Icon Badge */}
                            <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-xs flex items-center justify-center text-white/80 group-hover:text-white group-hover:bg-black/90 transition-all">
                                <FontAwesomeIcon icon={faExpand} className="text-xs" />
                            </div>

                            {/* Category Tag */}
                            <div className="absolute top-3 left-3">
                                <span className="inline-block px-2.5 py-1 text-[10px] sm:text-xs font-medium tracking-wider uppercase text-white bg-black/70 backdrop-blur-xs rounded border border-white/20">
                                    {item.category}
                                </span>
                            </div>
                        </div>

                        {/* Card Info */}
                        <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between bg-black/30">
                            <div>
                                <h3 className="text-base sm:text-lg font-light tracking-wide text-white mb-2 group-hover:text-gray-200 transition-colors">
                                    {item.title}
                                </h3>
                                <p className="text-xs text-gray-400 leading-relaxed line-clamp-2">
                                    {item.description}
                                </p>
                            </div>
                            <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-gray-400 group-hover:text-white transition-colors">
                                <span className="tracking-wider uppercase text-[11px]">View Photo</span>
                                <span>→</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Lightbox Modal */}
            {selectedIdx !== null && (
                <div
                    className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-fadeIn"
                    onClick={() => setSelectedIdx(null)}
                >
                    {/* Close Button */}
                    <button
                        onClick={() => setSelectedIdx(null)}
                        aria-label="Close modal"
                        className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-lg z-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>

                    {/* Prev Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIdx((selectedIdx - 1 + galleryItems.length) % galleryItems.length);
                        }}
                        aria-label="Previous photo"
                        className="hidden sm:flex absolute left-4 sm:left-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center text-base z-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>

                    {/* Next Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIdx((selectedIdx + 1) % galleryItems.length);
                        }}
                        aria-label="Next photo"
                        className="hidden sm:flex absolute right-4 sm:right-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center text-base z-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>

                    {/* Content Box */}
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="max-w-4xl w-full max-h-[90vh] flex flex-col items-center bg-black/60 rounded-xl overflow-hidden border border-white/15"
                    >
                        <div className="relative w-full flex items-center justify-center bg-black/80 max-h-[70vh] overflow-hidden">
                            <img
                                src={galleryItems[selectedIdx].image}
                                alt={galleryItems[selectedIdx].title}
                                className="max-h-[70vh] w-auto max-w-full object-contain"
                            />
                        </div>
                        <div className="w-full p-4 sm:p-6 bg-[#1a1a19] text-left">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="px-2 py-0.5 text-[10px] tracking-wider uppercase bg-white/10 text-gray-200 rounded">
                                    {galleryItems[selectedIdx].category}
                                </span>
                                <span className="text-xs text-gray-400">
                                    {selectedIdx + 1} of {galleryItems.length}
                                </span>
                            </div>
                            <h3 className="text-lg sm:text-xl font-light text-white tracking-wide mb-1">
                                {galleryItems[selectedIdx].title}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">
                                {galleryItems[selectedIdx].description}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PastEventsGallery;
